import * as React from "react";

import BugsnagPerformance from "@bugsnag/browser-performance";
import Bugsnag from "@bugsnag/js";
// eslint-disable-next-line import/order -- idk wtf is up with this
import BugsnagPluginReact from "@bugsnag/plugin-react";

import "./index.css";
import { Global, MantineProvider, MantineSize } from "@mantine/core";
import { Font as ReactPdfFont } from "@react-pdf/renderer";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

import B612Bold from "./data/fonts/b612/B612-Bold.ttf";
import B612Regular from "./data/fonts/b612/B612-Regular.ttf";
import B612MonoBold from "./data/fonts/b612/B612Mono-Bold.ttf";
import B612MonoRegular from "./data/fonts/b612/B612Mono-Regular.ttf";
import CourierPrimeBold from "./data/fonts/courier-prime/CourierPrime-Bold.ttf";
import CourierPrimeRegular from "./data/fonts/courier-prime/CourierPrime-Regular.ttf";
import InterBlack from "./data/fonts/inter/Inter-Black.ttf";
import InterBold from "./data/fonts/inter/Inter-Bold.ttf";
import InterExtraBold from "./data/fonts/inter/Inter-ExtraBold.ttf";
import InterMedium from "./data/fonts/inter/Inter-Medium.ttf";
import InterRegular from "./data/fonts/inter/Inter-Regular.ttf";
import InterSemiBold from "./data/fonts/inter/Inter-SemiBold.ttf";
import { persistor, store } from "./lib/state/store";
import { router } from "./router";

const customBreakpoints: Record<MantineSize, number> = {
  xs: 576,
  sm: 768,
  md: 992,
  lg: 1400,
  xl: 1900,
} as const;

Bugsnag.start({
  apiKey: "4683921dcca3075aeaa5b86c20b6c416",
  plugins: [new BugsnagPluginReact()],
  enabledReleaseStages: ["production"],
});
BugsnagPerformance.start({ apiKey: "4683921dcca3075aeaa5b86c20b6c416" });

const CustomFonts = (): JSX.Element => {
  return (
    <Global
      styles={[
        {
          "@font-face": {
            fontFamily: "B612",
            src: `url(${B612Regular}) format(truetype)`,
            fontWeight: 400,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "B612",
            src: `url(${B612Bold}) format(truetype)`,
            fontWeight: 700,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "B612 Mono",
            src: `url(${B612MonoRegular}) format(truetype)`,
            fontWeight: 400,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "B612 Mono",
            src: `url(${B612MonoBold}) format(truetype)`,
            fontWeight: 700,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Courier Prime",
            src: `url(${CourierPrimeRegular}) format(truetype)`,
            fontWeight: 400,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Courier Prime",
            src: `url(${CourierPrimeBold}) format(truetype)`,
            fontWeight: 700,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterRegular}) format(truetype)`,
            fontWeight: 400,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterMedium}) format(truetype)`,
            fontWeight: 500,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterSemiBold}) format(truetype)`,
            fontWeight: 600,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterBold}) format(truetype)`,
            fontWeight: 700,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterExtraBold}) format(truetype)`,
            fontWeight: 800,
            fontStyle: "normal",
          },
        },
        {
          "@font-face": {
            fontFamily: "Inter",
            src: `url(${InterBlack}) format(truetype)`,
            fontWeight: 900,
            fontStyle: "normal",
          },
        },
      ]}
    />
  );
};

ReactPdfFont.register({
  family: "B612",
  src: B612Regular,
  fontStyle: "normal",
  fontWeight: "normal",
});
ReactPdfFont.register({
  family: "B612",
  src: B612Bold,
  fontStyle: "normal",
  fontWeight: 700,
});
ReactPdfFont.register({
  family: "B612 Mono",
  src: B612MonoRegular,
  fontStyle: "normal",
  fontWeight: "normal",
});
ReactPdfFont.register({
  family: "B612 Mono",
  src: B612MonoBold,
  fontStyle: "normal",
  fontWeight: 700,
});
ReactPdfFont.register({
  family: "Courier Prime",
  src: CourierPrimeRegular,
  fontStyle: "normal",
  fontWeight: "normal",
});
ReactPdfFont.register({
  family: "Courier Prime",
  src: CourierPrimeBold,
  fontStyle: "normal",
  fontWeight: 700,
});
ReactPdfFont.register({
  family: "Inter",
  src: InterRegular,
  fontStyle: "normal",
  fontWeight: "normal",
});
ReactPdfFont.register({
  family: "Inter",
  src: InterMedium,
  fontStyle: "normal",
  fontWeight: 500,
});
ReactPdfFont.register({
  family: "Inter",
  src: InterSemiBold,
  fontStyle: "normal",
  fontWeight: 600,
});
ReactPdfFont.register({
  family: "Inter",
  src: InterBold,
  fontStyle: "normal",
  fontWeight: 700,
});
ReactPdfFont.register({
  family: "Inter",
  src: InterExtraBold,
  fontStyle: "normal",
  fontWeight: 800,
});
ReactPdfFont.register({
  family: "Inter",
  src: InterBlack,
  fontStyle: "normal",
  fontWeight: 900,
});

const ErrorBoundary =
  Bugsnag.getPlugin("react")?.createErrorBoundary(React) ??
  ((): null => {
    return null;
  });

export const App = (): JSX.Element => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MantineProvider
            withNormalizeCSS
            withGlobalStyles
            theme={{
              breakpoints: customBreakpoints,
              components: {
                Container: {
                  defaultProps: {
                    sizes: customBreakpoints,
                  },
                },
              },
              globalStyles: (theme) => ({
                a: {
                  color: theme.colors.cyan[8],
                },
              }),
            }}
          >
            <CustomFonts />
            <RouterProvider router={router} />
          </MantineProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
